import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAtozasLanguage } from '../../context/AtozasLanguageContext';
import { getAllLanguages } from '../../utils/languages';

/**
 * Custom LanguageSelector component that works with react-scripts
 * Uses hooks from atozas-traslate but implements JSX ourselves to avoid transpilation issues
 */
const LanguageSelector = ({ 
  className = '', 
  id = 'atozas-language-selector',
  onChange,
  variant = 'select',
  label = 'Language',
  showSelectedCode = false,
  enableSearch = true,
  ...otherProps 
}) => {
  const { language, setLanguage } = useAtozasLanguage();
  const languages = useMemo(() => getAllLanguages(), []); // Get all 100+ languages automatically

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedCode = (language || 'en');
  const selectedCodeUpper = selectedCode.toUpperCase();
  const selectedName = useMemo(() => {
    const match = languages.find((l) => l.code === selectedCode);
    return match?.name || selectedCodeUpper;
  }, [languages, selectedCode, selectedCodeUpper]);

  const filteredLanguages = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return languages;
    return languages.filter((l) => {
      const name = (l.name || '').toLowerCase();
      const code = (l.code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [languages, query]);

  // Handle language change
  const applyLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    
    // Call optional onChange callback if provided
    if (onChange && typeof onChange === 'function') {
      onChange(newLanguage);
    }
  };

  const handleNativeChange = (e) => {
    const newLanguage = e.target.value;
    applyLanguage(newLanguage);
    
    // CRITICAL: Also dispatch event for native select to ensure immediate update
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('language-changed', { 
        detail: { language: newLanguage } 
      }));
    }
  };

  // Close dropdown on outside click / Escape
  useEffect(() => {
    if (!isOpen) return;

    const onMouseDown = (e) => {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && !root.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  // Focus search on open
  useEffect(() => {
    if (!isOpen) return;
    if (!enableSearch) return;
    const t = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [enableSearch, isOpen]);

  const openDropdown = () => setIsOpen(true);
  const toggleDropdown = () => setIsOpen((v) => !v);

  const selectFromList = (code) => {
    applyLanguage(code);
    setIsOpen(false);
    setQuery('');
    
    // Force a re-render and trigger translation/restoration immediately
    // Dispatch a custom event to notify AutoTranslateWrapper
    // CRITICAL: This must happen for ALL languages including English
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('language-changed', { 
        detail: { language: code } 
      }));
    }
  };

  const renderChevron = () => (
    <svg
      className="w-5 h-5 text-gray-900"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  const renderSearchPanel = (mode = 'desktop') => (
    <div
      className={`${mode === 'mobile'
        ? 'fixed inset-x-0 top-0 bottom-0 z-[10050] flex w-screen max-w-screen flex-col overflow-hidden bg-white notranslate'
        : 'absolute right-0 mt-2 w-72 max-w-[85vw] rounded-xl border border-gray-200 bg-white shadow-lg z-50 overflow-hidden notranslate'
      }`}
      translate="no"
      role="dialog"
      aria-label="Language selector"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {enableSearch && (
        <div className="p-2 border-b border-gray-100">
          <input
            ref={searchInputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search language..."
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}

      <div className={`${mode === 'mobile' ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain' : 'max-h-72 overflow-auto'}`}>
        {filteredLanguages.length === 0 ? (
          <div className="px-3 py-3 text-sm text-gray-500">No languages found</div>
        ) : (
          <ul role="listbox" aria-label="Languages">
            {filteredLanguages.map((lang) => {
              const isSelected = lang.code === selectedCode;
              return (
                <li key={lang.code} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    onClick={() => selectFromList(lang.code)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-gray-50 ${
                      isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-800'
                    }`}
                  >
                    <span className="truncate">{lang.name}</span>
                    <span className="ml-3 text-xs text-gray-500">{lang.code.toUpperCase()}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  if (variant === 'pill') {
    // Backward-compatible behavior: even if parent doesn't pass showSelectedCode,
    // show "EN" when English is selected (requested UX).
    const isEnglish = selectedCode === 'en';
    const pillLabel = showSelectedCode ? selectedCodeUpper : (isEnglish ? 'EN' : label);

    return (
      <div ref={rootRef} className="relative inline-block notranslate" translate="no">
        <button
          type="button"
          onClick={toggleDropdown}
          className={`inline-flex items-center rounded-full border-2 border-gray-900 bg-white px-2 py-1.5 ${className}`.trim()}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={`${id}-listbox`}
          {...otherProps}
        >
          {/* Globe icon */}
          <svg
            className="w-4 h-4 text-gray-900"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.6 9h16.8M3.6 15h16.8M12 3c2.8 2.5 4.5 5.7 4.5 9S14.8 18.5 12 21c-2.8-2.5-4.5-5.7-4.5-9S9.2 5.5 12 3z"
            />
          </svg>

          <span className="mx-1.5 text-sm font-semibold text-gray-900 select-none">
            {pillLabel}
          </span>

          {renderChevron()}
        </button>

        {isOpen && (
          <>
            <div id={`${id}-listbox`} className="hidden sm:block">{renderSearchPanel('desktop')}</div>
            {typeof document !== 'undefined' && createPortal(
              <div className="sm:hidden fixed inset-0 z-[10049] bg-black/30" onMouseDown={() => {
                setIsOpen(false);
                setQuery('');
              }}>
                {renderSearchPanel('mobile')}
              </div>,
              document.body
            )}
          </>
        )}
      </div>
    );
  }

  // Default select-style trigger; if search is disabled, fall back to native <select>.
  if (!enableSearch) {
    return (
      <select
        id={id}
        value={language || 'en'}
        onChange={handleNativeChange}
        className={`atozas-language-selector ${className}`.trim()}
        {...otherProps}
      >
        {languages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div ref={rootRef} className="relative inline-block notranslate" translate="no">
      <button
        type="button"
        id={id}
        onClick={() => {
          if (!isOpen) openDropdown();
          else toggleDropdown();
        }}
        className={`inline-flex items-center justify-between gap-2 ${className}`.trim()}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${id}-listbox`}
        {...otherProps}
      >
        <span className="truncate">{selectedName}</span>
        {renderChevron()}
      </button>

      {isOpen && (
        <>
          <div id={`${id}-listbox`} className="hidden sm:block">{renderSearchPanel('desktop')}</div>
          {typeof document !== 'undefined' && createPortal(
            <div className="sm:hidden fixed inset-0 z-[10049] bg-black/30" onMouseDown={() => {
              setIsOpen(false);
              setQuery('');
            }}>
              {renderSearchPanel('mobile')}
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
};

export default LanguageSelector;

